import { Dispatch, SetStateAction, useCallback } from 'react';

import { prepareErrorMessage } from '#/features/wallet/ethereum';
import { logException } from '#/features/logging/logging';
import { useWalletState } from '#/features/wallet/wallet-context';
import {
  DepositState,
  useDepositState,
} from '#/features/deposit/deposit-context';
import { BridgedToken } from '#/features/paraclear';

import { fromQuantums } from '#/utils/quantums';
import { AsyncResult } from '#/utils/types';

import * as DepositEthereumService from './deposit-ethereum-service';

interface FetchEthereumBridgeAllowance {
  (token: BridgedToken): AsyncResult<void>;
}

export default function useFetchEthereumBridgeAllowance(): FetchEthereumBridgeAllowance {
  const [walletState] = useWalletState();
  const [depositState, setDepositState] = useDepositState();

  return useCallback(
    async function fetchEthereumBridgeAllowance(token: BridgedToken) {
      if (walletState.ethereumAddress == null) {
        throw new Error(
          'fetchEthereumBridgeAllowance called before ethereumAddress available',
        );
      }

      if (depositState.status !== 'idle') {
        throw new Error(
          `Deposit is already in progress (status is ${depositState.status}).`,
        );
      }

      const actions = prepareActions(setDepositState);
      actions.resetError();

      try {
        actions.setStatus('busy');

        const allowance = await DepositEthereumService.readBridgeAllowance(
          token,
          walletState.ethereumAddress,
        );
        const usdcAllowance = fromQuantums(allowance, token.decimals);
        actions.updateAllowance(usdcAllowance);

        actions.setStatus('idle');
        return { ok: true, data: undefined };
      } catch (err) {
        const description =
          'Failed to request available ethereum bridge allowance';
        const { message, isException } = prepareErrorMessage(description, err);
        actions.setError(message);
        actions.setStatus('idle');
        if (isException) {
          const error = new Error(description, { cause: err });
          logException(error);
          return { ok: false, error, reason: description };
        }
        return { ok: false, error: null, reason: description };
      }
    },
    [depositState.status, setDepositState, walletState.ethereumAddress],
  );
}

function prepareActions(setState: Dispatch<SetStateAction<DepositState>>) {
  function setStatus(status: DepositState['status']) {
    setState((state) => ({ ...state, status }));
  }

  function updateAllowance(allowance: DepositState['allowance']) {
    setState((state) => ({ ...state, allowance }));
  }

  const setError = (error: string) => {
    setState((state) => ({ ...state, error }));
  };

  const resetError = () => {
    setState((state) => ({ ...state, error: '' }));
  };

  return { setStatus, updateAllowance, setError, resetError };
}
